import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaPlay,
  FaChartBar,
  FaSpinner,
  FaSearch,
  FaCopy,
  FaUsers,
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import EntityAssignUsersModal from '../common/EntityAssignUsersModal';
import CloneNameModal from '../common/CloneNameModal';
import { formatDateTime } from '../../utils/formatUtils';
import {
  formatRunAsLabel,
  getLoggedInUserId,
  loggedInUserRunAsLabel,
  pickRecordId,
  runningUserApiRowToOption,
} from '../../utils/dashboardRunUserLabel';
import LookupLayout from '../layout/LookupLayout';
import { getEntitlementsSection, getLicenseTypeCodeFromSession } from '../../utils/entitlements';
import { canCreateDashboardByRole } from '../../utils/permissionCache';
import {
  createDashboardFormDefaults,
  formatDashboardOwner,
  labelToName,
  normalizeDashboardPayload,
  resolveDashboardOwnerId,
} from './dashboardFormUtils';
import './css/DashboardManager.css';

const DashboardManager = () => {
  const navigate = useNavigate();
  const canChangeDashboardOwner = getLicenseTypeCodeFromSession() === 'ADMIN';
  const dashboardEntitlements = getEntitlementsSection('dashboard', {});
  const allowCreateDashboardLicense = dashboardEntitlements.allowCreateDashboard !== false;
  const maxDashboards = Number(dashboardEntitlements.maxDashboards);
  const [roleAllowCreateDashboard, setRoleAllowCreateDashboard] = useState(true);
  const maxAssignedDashboardsPerUser = (() => {
    const n = Number(dashboardEntitlements.maxAssignedDashboardsPerUser);
    return Number.isFinite(n) && n > 0 ? n : 2;
  })();
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [assignDashboard, setAssignDashboard] = useState(null);
  const [cloneSource, setCloneSource] = useState(null);
  const [cloneError, setCloneError] = useState(null);
  const [cloningDashboardId, setCloningDashboardId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDashboards, setFilteredDashboards] = useState([]);
  const [formData, setFormData] = useState(createDashboardFormDefaults());
  const [runningUserOptions, setRunningUserOptions] = useState([]);
  const [runningUsersLoading, setRunningUsersLoading] = useState(false);

  const loadRunningUserOptions = useCallback(async () => {
    setRunningUsersLoading(true);
    try {
      const dashboardApi = new DataApi('dashboard');
      const res = await dashboardApi.get('/running-user-options?limit=500');
      const rows = res?.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
      const opts = rows.map(runningUserApiRowToOption).filter(Boolean);
      const byId = new Map(opts.map((o) => [o.id, o]));
      const me = getLoggedInUserId();
      if (me && !byId.has(me)) {
        byId.set(me, { id: me, label: loggedInUserRunAsLabel() });
      }
      setRunningUserOptions(
        Array.from(byId.values()).sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        )
      );
    } catch (e) {
      const me = getLoggedInUserId();
      setRunningUserOptions(me ? [{ id: me, label: loggedInUserRunAsLabel() }] : []);
    } finally {
      setRunningUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboards();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const allowed = await canCreateDashboardByRole();
      if (!cancelled) setRoleAllowCreateDashboard(allowed);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isDashboardLimitReached =
    Number.isFinite(maxDashboards) && maxDashboards >= 0 && dashboards.length >= maxDashboards;
  const canCreateDashboard =
    allowCreateDashboardLicense && roleAllowCreateDashboard && !isDashboardLimitReached;

  useEffect(() => {
    filterDashboards();
  }, [dashboards, searchTerm]);

  useEffect(() => {
    if (!showCreateModal) return;
    loadRunningUserOptions();
  }, [showCreateModal, loadRunningUserOptions]);

  const loadDashboards = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await new DataApi('dashboard').fetchAll();
      if (response.data.success) {
        setDashboards(response.data.data);
      } else {
        setError('Failed to load dashboards');
      }
    } catch (err) {
      console.error('Error loading dashboards:', err);
      setError('Error loading dashboards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterDashboards = () => {
    if (!searchTerm.trim()) {
      setFilteredDashboards(dashboards);
      return;
    }
    const q = searchTerm.toLowerCase();
    setFilteredDashboards(
      dashboards.filter((dashboard) => {
        const owner = formatDashboardOwner(dashboard).toLowerCase();
        return (
          dashboard.name.toLowerCase().includes(q) ||
          (dashboard.label && dashboard.label.toLowerCase().includes(q)) ||
          (dashboard.description && dashboard.description.toLowerCase().includes(q)) ||
          owner.includes(q)
        );
      })
    );
  };

  const handleOwnerLookupChange = (userId, userLabel) => {
    const uid = userId ? String(userId).trim() : '';
    setFormData((prev) => ({
      ...prev,
      owner_id: uid,
      owner_label: uid ? (userLabel || '').trim() : '',
    }));
  };

  const handleCreateDashboard = async (e) => {
    e.preventDefault();
    try {
      const payload = normalizeDashboardPayload(formData);
      if (payload.view_type === 'STATIC_USER' && !payload.running_user_id) {
        setError('Please select a user for “Fixed user” dashboards.');
        return;
      }
      if (canChangeDashboardOwner && !payload.ownerid) {
        setError('Please select an owner for this dashboard.');
        return;
      }
      const response = await new DataApi('dashboard').create(payload);
      if (response.data.success) {
        const id = pickRecordId(response.data.data);
        setShowCreateModal(false);
        setFormData(createDashboardFormDefaults());
        if (id) {
          navigate(`/dashboard/${id}/canvas`);
        } else {
          loadDashboards();
        }
      } else {
        setError('Failed to create dashboard');
      }
    } catch (err) {
      console.error('Error creating dashboard:', err);
      setError('Error creating dashboard. Please try again.');
    }
  };

  const openCreateModal = () => {
    setFormData(createDashboardFormDefaults());
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setFormData(createDashboardFormDefaults());
  };

  const handleLabelChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      label: value,
      name: labelToName(value),
    }));
  };

  const handleViewTypeChange = (viewType) => {
    setFormData((prev) => {
      const next = { ...prev, view_type: viewType };
      if (viewType === 'STATIC_USER') {
        const cur = prev.running_user_id && String(prev.running_user_id).trim();
        next.running_user_id = cur || getLoggedInUserId() || '';
      }
      return next;
    });
  };

  const openDashboardEditor = (dashboard) => {
    const id = pickRecordId(dashboard);
    if (!id) return;

    const me = getLoggedInUserId();
    const ownerId = resolveDashboardOwnerId(dashboard);
    const isOwner = !!me && !!ownerId && String(me) === String(ownerId);

    if (!canChangeDashboardOwner && !isOwner) {
      window.alert('You can edit only those dashboards where you are the owner.');
      return;
    }

    navigate(`/dashboard/${id}/canvas`);
  };

  const handleRunDashboard = (dashboardId) => {
    navigate(`/dashboard/${dashboardId}/run`);
  };

  const openCloneModal = (dashboard) => {
    const sourceLabel = dashboard.label || dashboard.name || 'Dashboard';
    setCloneError(null);
    setCloneSource({
      dashboard,
      defaultLabel: `${sourceLabel} (Copy)`,
      defaultName: labelToName(`${sourceLabel} (Copy)`),
    });
  };

  const closeCloneModal = () => {
    if (cloningDashboardId) return;
    setCloneSource(null);
    setCloneError(null);
  };

  const handleConfirmCloneDashboard = async ({ label, name }) => {
    const dashboard = cloneSource?.dashboard;
    const dashboardId = dashboard ? pickRecordId(dashboard) : '';
    if (!dashboardId) return;

    const normalized = labelToName(name || label);
    if (
      dashboards.some(
        (d) => labelToName(d.name) === normalized && String(d.id) !== String(dashboardId)
      )
    ) {
      setCloneError(`A dashboard named "${normalized}" already exists.`);
      return;
    }

    try {
      setCloningDashboardId(dashboardId);
      setCloneError(null);
      const res = await new DataApi('dashboard').post(`/${dashboardId}/clone`, { label, name });
      const cloned = res?.data?.data;
      const count = res?.data?.widgetCount ?? 0;
      setCloneSource(null);
      await loadDashboards();
      if (
        cloned?.id &&
        window.confirm(
          `Created "${cloned.label || cloned.name}" with ${count} widget(s).\n\nOpen the new dashboard canvas now?`
        )
      ) {
        navigate(`/dashboard/${cloned.id}/canvas`);
      }
    } catch (err) {
      setCloneError(
        err?.response?.data?.message || 'Failed to clone dashboard. Please try again.'
      );
    } finally {
      setCloningDashboardId(null);
    }
  };

  const handleDeleteDashboard = async (dashboard) => {
    if (
      !window.confirm('Are you sure you want to delete this dashboard? This action cannot be undone.')
    ) {
      return;
    }
    try {
      const response = await new DataApi('dashboard').delete(`/${dashboard.id}`);
      if (response.data.success) {
        loadDashboards();
      } else {
        setError('Failed to delete dashboard');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Error deleting dashboard. Please try again.');
    }
  };

  const runningUserSelectOptions =
    runningUserOptions.length > 0
      ? runningUserOptions
      : (() => {
          const me = getLoggedInUserId();
          return me ? [{ id: me, label: loggedInUserRunAsLabel() }] : [];
        })();

  if (loading) {
    return (
      <div className="dashboard-manager">
        <div className="dashboard-loading-container">
          <FaSpinner className="dashboard-spinner" />
          <p>Loading dashboards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-manager">
      <div className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="dashboard-header-title">
            <h1>
              <FaChartBar className="dashboard-header-icon" />
              Dashboard Manager
            </h1>
            <p className="dashboard-header-subtitle">Create and manage your dashboards</p>
          </div>
          {canCreateDashboard && (
            <button
              type="button"
              className="dashboard-create-btn"
              onClick={openCreateModal}
            >
              <FaPlus />
              Create Dashboard
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="dashboard-error-banner">
          <p>{error}</p>
          <button type="button" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      <div className="dashboard-search-filter-section">
        <div className="dashboard-search-box">
          <FaSearch className="dashboard-search-icon" />
          <input
            type="text"
            placeholder="Search dashboards..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="dashboard-search-input"
          />
        </div>
      </div>

      <div className="dashboard-dashboards-container">
        {filteredDashboards.length === 0 ? (
          <div className="dashboard-empty-state">
            <FaChartBar className="dashboard-empty-icon" />
            <h3>No dashboards found</h3>
            <p>
              {searchTerm
                ? 'No dashboards match your search criteria.'
                : 'Get started by creating your first dashboard.'}
            </p>
            {!searchTerm && canCreateDashboard && (
              <button className="dashboard-create-btn" onClick={openCreateModal}>
                <FaPlus />
                Create Dashboard
              </button>
            )}
          </div>
        ) : (
          <div className="dashboard-table-container">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Name</th>
                  <th>Owner</th>
                  <th>Users</th>
                  <th>View as</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDashboards.map((dashboard) => (
                  <tr key={dashboard.id} className="dashboard-table-row">
                    <td className="dashboard-table-label">
                      {dashboard.label || dashboard.name}
                    </td>
                    <td className="dashboard-table-name">{dashboard.name || '-'}</td>
                    <td className="dashboard-table-owner">
                      {formatDashboardOwner(dashboard)}
                    </td>
                    <td className="dashboard-table-name">
                      {dashboard.assigned_user_count ?? 0}
                    </td>
                    <td className="dashboard-table-run-as">
                      {dashboard.view_type === 'LOGGED_IN_USER'
                        ? 'Logged-in viewer'
                        : dashboard.running_user_id
                          ? formatRunAsLabel(
                              dashboard.running_user_full_name,
                              dashboard.running_user_username,
                              dashboard.running_user_role_name
                            )
                          : 'Fixed user (not set)'}
                    </td>
                    <td className="dashboard-table-description">
                      {dashboard.description || '-'}
                    </td>
                    <td className="dashboard-table-actions">
                      <button
                        type="button"
                        className="dashboard-action-btn dashboard-users-btn"
                        onClick={() => setAssignDashboard(dashboard)}
                        title="Assign users"
                      >
                        <FaUsers />
                      </button>
                      <button
                        type="button"
                        className="dashboard-action-btn dashboard-run-btn"
                        onClick={() => handleRunDashboard(dashboard.id)}
                        title="Run Dashboard"
                      >
                        <FaPlay />
                      </button>
                      {canCreateDashboard && (
                        <button
                          type="button"
                          className="dashboard-action-btn dashboard-clone-btn"
                          onClick={() => openCloneModal(dashboard)}
                          title="Clone dashboard and widgets"
                          disabled={cloningDashboardId === dashboard.id}
                        >
                          {cloningDashboardId === dashboard.id ? (
                            <FaSpinner className="fa-spin" />
                          ) : (
                            <FaCopy />
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        className="dashboard-action-btn dashboard-edit-btn"
                        onClick={() => openDashboardEditor(dashboard)}
                        title="Edit dashboard and widgets"
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="dashboard-action-btn dashboard-delete-btn"
                        onClick={() => handleDeleteDashboard(dashboard)}
                        title="Delete Dashboard"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="dashboard-modal-overlay">
          <div className="dashboard-modal">
            <div className="dashboard-modal-header">
              <h2>Create New Dashboard</h2>
              <button type="button" className="dashboard-close-btn" onClick={closeCreateModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreateDashboard} className="dashboard-modal-form">
              <div className="dashboard-hint-info">
                <FaChartBar className="dashboard-hint-icon" />
                <p>
                  After creating, you will open the widget canvas. Use the gear icon on the canvas
                  to edit label, owner, and view-as settings.
                </p>
              </div>
              <div className="dashboard-form-group">
                <label htmlFor="label">Dashboard Label *</label>
                <input
                  type="text"
                  id="label"
                  value={formData.label}
                  onChange={(e) => handleLabelChange(e.target.value)}
                  required
                  placeholder="Enter dashboard label"
                />
              </div>
              <div className="dashboard-form-group">
                <label htmlFor="name">Dashboard Name *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Auto-populated from label"
                />
              </div>
              <div className="dashboard-form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              {canChangeDashboardOwner && (
                <div className="dashboard-form-group">
                  <label htmlFor="create-owner-lookup">Owner *</label>
                  <LookupLayout
                    objectName="user"
                    searchFields="firstname,lastname,email,username"
                    displayFields="firstname,lastname,email"
                    value={formData.owner_id}
                    defaultLabel={formData.owner_label}
                    onChange={handleOwnerLookupChange}
                    placeholder="Search owner…"
                    dropdownZIndex={10100}
                    activeUsersOnly
                  />
                </div>
              )}
              <div className="dashboard-form-group">
                <label htmlFor="view_type">Running user (view as)</label>
                <select
                  id="view_type"
                  value={formData.view_type}
                  onChange={(e) => handleViewTypeChange(e.target.value)}
                  className="dashboard-form-select"
                >
                  <option value="STATIC_USER">Fixed user</option>
                  <option value="LOGGED_IN_USER">Logged-in viewer</option>
                </select>
              </div>
              {formData.view_type === 'STATIC_USER' && (
                <div className="dashboard-form-group">
                  <label htmlFor="running_user_id">Run dashboard as user *</label>
                  <select
                    id="running_user_id"
                    value={formData.running_user_id}
                    onChange={(e) =>
                      setFormData({ ...formData, running_user_id: e.target.value })
                    }
                    className="dashboard-form-select"
                    required
                    disabled={runningUserSelectOptions.length === 0}
                  >
                    {runningUserSelectOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {runningUsersLoading && (
                    <p className="small text-muted mt-1 mb-0">Loading user list…</p>
                  )}
                </div>
              )}
              <div className="dashboard-modal-actions">
                <button type="button" className="dashboard-cancel-btn" onClick={closeCreateModal}>
                  Cancel
                </button>
                <button type="submit" className="dashboard-save-btn">
                  Create &amp; edit widgets
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <EntityAssignUsersModal
        show={!!assignDashboard}
        entity={assignDashboard}
        onClose={() => setAssignDashboard(null)}
        onSaved={loadDashboards}
        apiResource="dashboard"
        entityTypeLabel="dashboard"
        assignmentIdField="entity_id"
        exclusiveAssignment={false}
        maxAssignedPerUser={maxAssignedDashboardsPerUser}
        introText="Admins see all dashboards. Owners always see their own dashboards. Other users need to be assigned here to get access."
      />

      <CloneNameModal
        show={!!cloneSource}
        entityType="dashboard"
        sourceLabel={cloneSource?.dashboard?.label || cloneSource?.dashboard?.name || ''}
        defaultLabel={cloneSource?.defaultLabel}
        defaultName={cloneSource?.defaultName}
        deriveNameFromLabel={labelToName}
        saving={!!cloningDashboardId}
        error={cloneError}
        onClose={closeCloneModal}
        onConfirm={handleConfirmCloneDashboard}
      />
    </div>
  );
};

export default DashboardManager;
